
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CustomizationSelection {
  customization_id: string;
  selected_options: string[];
  text_input?: string;
}

interface CartItem {
  id: string;
  menu_item_id: string;
  name: string;
  price: number;
  quantity: number;
  image_url?: string;
  restaurant_id: string;
  restaurant_name: string;
  referral_post_id?: string;
  customizations?: CustomizationSelection[];
  cart_type: 'restaurant' | 'marketplace';
  is_complementary?: boolean; // Flag for complementary items from other restaurants
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'id' | 'quantity'> & { quantity?: number; referral_post_id?: string }) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  updateQuantity: (id: string, quantity: number) => Promise<void>;
  clearCart: (cartType?: 'restaurant' | 'marketplace') => Promise<void>;
  total: number;
  itemCount: number;
  deliveryType: 'delivery' | 'pickup';
  setDeliveryType: (type: 'delivery' | 'pickup') => void;
  refreshCart: () => Promise<void>;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getRestaurantId: () => string | null;
  getReferralPostId: () => string | null;
  orderType: 'delivery' | 'pickup';
  setOrderType: (type: 'delivery' | 'pickup') => void;
  cartType: 'restaurant' | 'marketplace';
  setCartType: (type: 'restaurant' | 'marketplace') => void;
  filteredItems: CartItem[];
  getRestaurantCartCount: () => number;
  getMarketplaceCartCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [deliveryType, setDeliveryType] = useState<'delivery' | 'pickup'>('delivery');
  const [cartType, setCartType] = useState<'restaurant' | 'marketplace'>('restaurant');
  const { toast } = useToast();

  // Filter items based on current cart type
  const filteredItems = items.filter(item => item.cart_type === cartType);

  const loadFromLocalStorage = useCallback(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        console.log('Cart loaded from localStorage:', parsedCart);
        setItems(parsedCart);
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        setItems([]);
      }
    } else {
      setItems([]);
    }
  }, []);

  const refreshCart = useCallback(async () => {
    console.log('Refreshing cart...');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        console.log('User found, loading cart from database');
        const { data, error } = await supabase
          .from('cart_items')
          .select('*')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error loading cart from database:', error);
          loadFromLocalStorage();
        } else {
          console.log('Cart loaded from database:', data);
          // Parse customizations from JSON back to array
          const parsedItems = (data || []).map(item => ({
            ...item,
            cart_type: (item.cart_type === 'marketplace' || item.cart_type === 'restaurant') ? item.cart_type : 'restaurant' as 'restaurant' | 'marketplace',
            customizations: typeof item.customizations === 'string' 
              ? JSON.parse(item.customizations) 
              : (item.customizations || [])
          }));
          // Guard: if DB returns empty but we have a local cart, keep local
          // (prevents wiping items that were just added but not yet synced).
          const localRaw = localStorage.getItem('cart');
          const localItems = localRaw ? JSON.parse(localRaw) : [];
          if (parsedItems.length === 0 && localItems.length > 0) {
            console.log('DB empty but local cart has items — keeping local');
            setItems(localItems);
          } else {
            setItems(parsedItems);
            localStorage.setItem('cart', JSON.stringify(parsedItems));
          }
        }
      } else {
        console.log('No user found, loading cart from localStorage');
        loadFromLocalStorage();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
      loadFromLocalStorage();
    }
  }, [loadFromLocalStorage]);

  useEffect(() => {
    const initializeCart = async () => {
      console.log('Initializing cart...');
      
      const savedDeliveryType = localStorage.getItem('deliveryType') as 'delivery' | 'pickup';
      if (savedDeliveryType) {
        setDeliveryType(savedDeliveryType);
      }

      await refreshCart();
    };

    initializeCart();
  }, [refreshCart]);

  useEffect(() => {
    localStorage.setItem('deliveryType', deliveryType);
  }, [deliveryType]);

  const saveToLocalStorage = (cartItems: CartItem[]) => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  };

  const saveToDatabase = async (cartItems: CartItem[]) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        await supabase
          .from('cart_items')
          .delete()
          .eq('user_id', user.id);

        if (cartItems.length > 0) {
          const itemsToInsert = cartItems.map(item => ({
            id: item.id,
            user_id: user.id,
            menu_item_id: item.menu_item_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            restaurant_id: item.restaurant_id,
            restaurant_name: item.restaurant_name,
            image_url: item.image_url,
            customizations: JSON.stringify(item.customizations || []),
            referral_post_id: item.referral_post_id || null,
            cart_type: item.cart_type,
          }));

          const { error } = await supabase
            .from('cart_items')
            .insert(itemsToInsert);

          if (error) {
            console.error('Error saving cart to database:', error);
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error saving cart to database:', error);
    }
  };

  const addItem = async (newItem: Omit<CartItem, 'id' | 'quantity'> & { quantity?: number }) => {
    try {
      console.log('Adding item to cart:', newItem);
      
      const quantity = newItem.quantity || 1;
      const itemCartType = newItem.cart_type;
      let updatedItems: CartItem[];

      // Filter existing items by cart type
      const currentCartItems = items.filter(item => item.cart_type === itemCartType);
      const primaryRestaurantItems = currentCartItems.filter(item => !item.is_complementary);

      // Allow complementary items from different restaurants
      if (newItem.is_complementary) {
        const existingItemIndex = items.findIndex(
          item => item.menu_item_id === newItem.menu_item_id && item.cart_type === itemCartType
        );

        if (existingItemIndex >= 0) {
          updatedItems = items.map((item, index) =>
            index === existingItemIndex
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        } else {
          const itemWithId = {
            ...newItem,
            id: `${newItem.menu_item_id}_${Date.now()}_${Math.random()}`,
            quantity
          };
          updatedItems = [...items, itemWithId];
        }
      } else {
        // Regular item - check if switching primary restaurant
        if (primaryRestaurantItems.length > 0 && primaryRestaurantItems[0].restaurant_id !== newItem.restaurant_id) {
          // Clear existing cart of this type first
          await clearCart(itemCartType);
          
          toast({
            title: "Cart cleared",
            description: `Previous ${itemCartType} items removed as you can only order from one ${itemCartType === 'restaurant' ? 'restaurant' : 'seller'} at a time`,
          });
          const itemWithId = {
            ...newItem,
            id: `${newItem.menu_item_id}_${Date.now()}`,
            quantity
          };
          updatedItems = [...items.filter(item => item.cart_type !== itemCartType), itemWithId];
        } else {
          const existingItemIndex = items.findIndex(
            item => item.menu_item_id === newItem.menu_item_id && item.cart_type === itemCartType
          );

          if (existingItemIndex >= 0) {
            updatedItems = items.map((item, index) =>
              index === existingItemIndex
                ? { ...item, quantity: item.quantity + quantity }
                : item
            );
          } else {
            const itemWithId = {
              ...newItem,
              id: `${newItem.menu_item_id}_${Date.now()}_${Math.random()}`,
              quantity
            };
            updatedItems = [...items, itemWithId];
          }
        }
      }

      console.log('Cart updated to:', updatedItems);
      setItems(updatedItems);
      saveToLocalStorage(updatedItems);
      await saveToDatabase(updatedItems);

      // Toast removed per user request - no popup when adding to cart
    } catch (error) {
      console.error('Error adding item to cart:', error);
    }
  };

  const removeItem = async (id: string) => {
    try {
      const updatedItems = items.filter(item => item.id !== id);
      setItems(updatedItems);
      saveToLocalStorage(updatedItems);
      await saveToDatabase(updatedItems);
    } catch (error) {
      console.error('Error removing item from cart:', error);
      toast({
        title: "Error",
        description: "Failed to remove item from cart",
        variant: "destructive",
      });
    }
  };

  const updateQuantity = async (id: string, quantity: number) => {
    try {
      if (quantity <= 0) {
        await removeItem(id);
        return;
      }

      const updatedItems = items.map(item =>
        item.id === id ? { ...item, quantity } : item
      );
      
      setItems(updatedItems);
      saveToLocalStorage(updatedItems);
      await saveToDatabase(updatedItems);
    } catch (error) {
      console.error('Error updating item quantity:', error);
      toast({
        title: "Error",
        description: "Failed to update item quantity",
        variant: "destructive",
      });
    }
  };

  const clearCart = async (specificCartType?: 'restaurant' | 'marketplace') => {
    try {
      const typeTolear = specificCartType || cartType;
      const remainingItems = items.filter(item => item.cart_type !== typeTolear);
      
      setItems(remainingItems);
      saveToLocalStorage(remainingItems);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('cart_items')
          .delete()
          .eq('user_id', user.id)
          .eq('cart_type', typeTolear);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast({
        title: "Error",
        description: "Failed to clear cart",
        variant: "destructive",
      });
    }
  };

  const getTotalItems = () => {
    return filteredItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return filteredItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getRestaurantId = () => {
    // Return the primary restaurant (non-complementary item)
    const primaryItem = filteredItems.find(item => !item.is_complementary);
    return primaryItem ? primaryItem.restaurant_id : (filteredItems.length > 0 ? filteredItems[0].restaurant_id : null);
  };

  const getReferralPostId = () => {
    return filteredItems.length > 0 ? filteredItems[0].referral_post_id || null : null;
  };

  const getRestaurantCartCount = () => {
    return items.filter(item => item.cart_type === 'restaurant').reduce((sum, item) => sum + item.quantity, 0);
  };

  const getMarketplaceCartCount = () => {
    return items.filter(item => item.cart_type === 'marketplace').reduce((sum, item) => sum + item.quantity, 0);
  };

  const total = filteredItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = filteredItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items: filteredItems,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      total,
      itemCount,
      deliveryType,
      setDeliveryType,
      refreshCart,
      getTotalItems,
      getTotalPrice,
      getRestaurantId,
      getReferralPostId,
      orderType: deliveryType,
      setOrderType: setDeliveryType,
      cartType,
      setCartType,
      filteredItems,
      getRestaurantCartCount,
      getMarketplaceCartCount,
    }}>
      {children}
    </CartContext.Provider>
  );
};
